import React, { useState, useEffect, useCallback } from 'react';
import { SidebarProvider } from "@/components/ui/sidebar";
import StudentHeader from "./StudentHeader";
import StudentSidebar from "./StudentSidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MessageSquare, GraduationCap, Users, Send, ArrowLeft, Bell, Mail, Smartphone, Clock, Eye, Search, ChevronDown, Loader2, UserCheck, CheckCircle, History, FileText, Download } from "lucide-react";
// Blue Loader component
const BlueLoader = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
  </div>
);
import { db, auth } from "../firebase/config";
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, doc, serverTimestamp, limit, writeBatch, arrayUnion, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import sanitizeHtml from 'sanitize-html';

interface CommunicationCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  recipients: string;
  recipientCount: number;
  disabled?: boolean;
}

interface Memo {
  id: string;
  title: string;
  description: string;
  fileUrl: string;
  fileName: string;
  fileType: string;
  targetAudience: 'staff' | 'teacher-admin' | 'student' | 'parent' | 'everyone';
  createdAt: string;
  senderEmail: string;
  recipientCount: number;
  readBy: string[];
}

interface Conversation {
  id: string;
  title: string;
  allowedEmails: string[];
  participantRoles: string[];
  type: '1:1';
  lastMessage: { content: string; senderEmail: string; timestamp: string; priority?: string } | null;
  lastUpdatedAt: string;
  recipients: string;
  recipientCount: number;
  hasReceivedMessages: boolean;
  schoolID: string;
  unreadCount: number;
}

interface Message {
  id: string;
  content: string;
  senderEmail: string;
  timestamp: string;
  readBy: string[];
  priority: string;
  deliveryMethod: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  subject?: string;
  studentName?: string;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}

interface StudentData {
  firstName: string;
  lastName: string;
  email: string;
  guardians: { name: string; email: string }[];
  status: string;
  studentUid: string;
}

// Utility function to truncate recipient information
const truncateRecipients = (recipients: string, maxLength: number = 20): string => {
  if (recipients.length <= maxLength) return recipients;
  return recipients.substring(0, maxLength) + '...';
};

const truncateMessage = (content: string, maxLength: number = 80): string => {
  if (!content) return '';
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength) + '...';
};

const StudentCommunication: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState("overview");
  const [selectedCategory, setSelectedCategory] = useState<CommunicationCategory | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [priority, setPriority] = useState("normal");
  const [deliveryMethod, setDeliveryMethod] = useState("app");
  const [schoolID, setSchoolID] = useState("");
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [guardians, setGuardians] = useState<User[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState("");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientType, setRecipientType] = useState<"teacher" | "guardian" | null>(null);
  const [returnView, setReturnView] = useState<"received-messages" | "sent-messages">("received-messages");
  const [unreadCount, setUnreadCount] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  
  // Memo States
  const [activeTab, setActiveTab] = useState<"messages" | "memos">("messages");
  const [selectedMemo, setSelectedMemo] = useState<Memo | null>(null);
  const [activeMemoCategory, setActiveMemoCategory] = useState<string | null>(null);
  const [memos, setMemos] = useState<Memo[]>([]);
  const memoCategories: CommunicationCategory[] = [
    { id: "students", title: "Student Memos", description: "Targeted at students only", icon: Users, recipientCount: 0, color: "bg-orange-500", recipients: "All students" },
    { id: "everyone", title: "Whole-School Memos", description: "General Notices for everyone", icon: Users, recipientCount: 0, color: "bg-gray-500", recipients: "Staff, Students, and Parents" },
  ];

  const [communicationCategories, setCommunicationCategories] = useState<CommunicationCategory[]>([
    { id: "teachers", title: "Communicate with Teachers", description: "Send messages to a teacher", icon: GraduationCap, color: "bg-green-500", recipients: "Teacher", recipientCount: 0 },
    { id: "guardians", title: "Communicate with Guardians", description: "Send messages to a guardian", icon: Users, color: "bg-blue-500", recipients: "Guardian", recipientCount: 0 },
  ]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idTokenResult = await getIdTokenResult(user, true);
          const claims = idTokenResult.claims as CustomClaims;
          const aff = claims.defaultAffiliation || claims.affiliations?.find(a => a.role === "student" && a.schoolID);
          if (claims.schoolID && aff?.role === "student" && aff?.schoolID === claims.schoolID) {
            setSchoolID(aff.schoolID);
            setHasValidClaims(true);
          } else {
            throw new Error("Invalid student claims");
          }
        } catch (error: any) {
          console.error("Error verifying user claims:", error);
          // Check if it's a network error
          const isNetworkError = error?.code === 'auth/network-request-failed' ||
                                error?.message?.includes('ERR_NAME_NOT_RESOLVED') ||
                                error?.message?.includes('network-request-failed') ||
                                error?.message?.includes('network');
          
          if (isNetworkError) {
            // Don't logout on network errors, just show a warning
            toast({ 
              title: "Network Error", 
              description: "Unable to verify permissions due to network issues. Please check your connection.", 
              variant: "destructive", 
              duration: 5000 
            });
          } else {
            // Only logout for non-network errors
            toast({ title: "Missing Permissions", description: "You lack permissions to access communications.", variant: "destructive", duration: 5000 });
            navigate("/login-page");
          }
        }
      } else {
        // Don't redirect here - let App.tsx global routing handle it
        // This prevents incorrect redirects during network errors
        console.log("StudentCommunication: No user found, deferring to App.tsx routing");
      }
      setIsAuthLoading(false);
    });
    return () => unsubscribe();
  }, [navigate, toast]);

  const fetchUsers = useCallback(async () => {
    if (!schoolID) return;
    try {
      const teachersRef = collection(db, `schools/${schoolID}/teachers`);
      const teacherSnapshot = await getDocs(teachersRef);
      const teacherList: User[] = teacherSnapshot.docs.map(doc => {
        const data = doc.data();
        return { id: doc.id, name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unknown', email: sanitizeHtml(data.email || ''), role: 'school_staff', subject: data.subjectsOfExpertise?.join(', ') || 'N/A' };
      });

      const guardianEmails = new Set<string>();
      const guardianList: User[] = [];
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const studentSnapshot = await getDocs(query(studentsRef, where("status", "==", "Active")));
      const invitationsRef = collection(db, 'invitations');
      const guardianInvites = await getDocs(query(invitationsRef, where('role', '==', 'guardian'), where('schoolID', '==', schoolID), where('status', '==', 'accepted')));
      const guardianAuthIds = new Map<string, string>();
      guardianInvites.forEach(doc => {
        const data = doc.data();
        if (data.invitedUid || data.guardianUid) guardianAuthIds.set(data.email, data.invitedUid || data.guardianUid);
      });

      studentSnapshot.forEach(doc => {
        const data = doc.data() as StudentData;
        data.guardians?.forEach(guardian => {
          if (guardian.email && !guardianEmails.has(guardian.email)) {
            guardianEmails.add(guardian.email);
            guardianList.push({
              id: guardianAuthIds.get(guardian.email) || guardian.email,
              name: guardian.name || 'Unknown Guardian',
              email: sanitizeHtml(guardian.email),
              role: 'guardian',
              studentName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unknown Student',
            });
          }
        });
      });

      setTeachers(teacherList);
      setGuardians(guardianList);
      setCommunicationCategories(prev => prev.map(category => ({
        ...category,
        recipientCount: category.id === "teachers" ? teacherList.length : guardianList.length,
        disabled: category.id === "teachers" ? teacherList.length === 0 : guardianList.length === 0,
      })));
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({ title: "Error", description: "Failed to load users.", variant: "destructive", duration: 5000 });
    }
  }, [schoolID, toast]);

  const fetchConversations = useCallback(() => {
    if (!schoolID || !auth.currentUser?.email) {
      console.log("Skipping fetchConversations: missing schoolID or user email", { schoolID, email: auth.currentUser?.email });
      return () => {};
    }

    console.log('🔍 Student fetchConversations - User email:', auth.currentUser.email, 'SchoolID:', schoolID);

    const convCollection = `schools/${schoolID}/conversations`;
    const q = query(
      collection(db, convCollection),
      where("allowedEmails", "array-contains", auth.currentUser.email),
      where("schoolID", "==", schoolID),
      orderBy("lastUpdatedAt", "desc"),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      console.log('📬 Student received conversations snapshot - Count:', snapshot.docs.length);
      snapshot.docs.forEach(d => console.log('  - Conversation:', d.id, 'allowedEmails:', d.data().allowedEmails, 'title:', d.data().title));
      
      const convList: Conversation[] = [];
      let totalUnread = 0;
      
      // Process each conversation to get unread counts
      for (const doc of snapshot.docs) {
        const data = doc.data();
        const hasReceivedMessages = data.lastMessage && data.lastMessage.senderEmail !== auth.currentUser?.email;
        
        // Count actual unread messages from the messages subcollection
        let unreadForConv = 0;
        if (hasReceivedMessages) {
          try {
            const messagesRef = collection(db, `schools/${schoolID}/conversations/${doc.id}/messages`);
            const unreadQuery = query(messagesRef, where('senderEmail', '!=', auth.currentUser?.email || ''));
            const messagesSnapshot = await getDocs(unreadQuery);
            
            messagesSnapshot.forEach((msgDoc) => {
              const msgData = msgDoc.data();
              if (!msgData.readBy?.includes(auth.currentUser?.email || '')) {
                unreadForConv++;
              }
            });
            
            totalUnread += unreadForConv;
          } catch (error) {
            console.error(`Error counting unread for conversation ${doc.id}:`, error);
          }
        }

        const lastMessage = data.lastMessage;
        let title = data.title || '';
        if (hasReceivedMessages && lastMessage?.senderEmail) {
          title = `Received from ${lastMessage.senderEmail.split('@')[0]}`;
        }

        convList.push({
          id: doc.id,
          title,
          allowedEmails: data.allowedEmails || [],
          participantRoles: data.participantRoles || [],
          type: '1:1',
          lastMessage: lastMessage ? {
            content: lastMessage.content || '',
            senderEmail: lastMessage.senderEmail || '',
            timestamp: lastMessage.timestamp?.toDate()?.toLocaleString() || '',
          } : null,
          lastUpdatedAt: data.lastUpdatedAt?.toDate()?.toLocaleString() || '',
          recipients: data.recipients || '',
          recipientCount: data.recipientCount || 0,
          hasReceivedMessages,
          schoolID: data.schoolID || schoolID,
          unreadCount: unreadForConv,
        });
      }
      setConversations(convList);
      setUnreadCount(totalUnread);
    }, (error) => {
      console.error("Error fetching conversations:", error);
      toast({ title: "Error", description: "Failed to load conversations.", variant: "destructive", duration: 5000 });
    });

    return unsubscribe;
  }, [schoolID, toast]);

  const fetchMemos = useCallback(() => {
    if (!schoolID) return () => {};
    
    const memosRef = collection(db, `schools/${schoolID}/memos`);
    const q = query(memosRef, orderBy("createdAt", "desc"), limit(50));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const memoList: Memo[] = snapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title,
        description: doc.data().description,
        fileUrl: doc.data().fileUrl,
        fileName: doc.data().fileName,
        fileType: doc.data().fileType,
        targetAudience: doc.data().targetAudience,
        createdAt: doc.data().createdAt?.toDate()?.toLocaleString() || '',
        senderEmail: doc.data().senderEmail,
        recipientCount: doc.data().recipientCount || 0,
        readBy: doc.data().readBy || [],
      }));
      // Filter memos relevant to student
      const relevantMemos = memoList.filter(m => 
        ['students', 'everyone'].includes(m.targetAudience)
      );
      setMemos(relevantMemos);
    });
    return unsubscribe;
  }, [schoolID]);

  const handleViewMemo = async (memo: Memo) => {
    setSelectedMemo(memo);
    const userEmail = auth.currentUser?.email;
    // Assuming memo has readBy field, if not we need to add it to interface
    // For now, let's assume it does or we just skip if not present in type
    // We need to update Memo interface first
    if (userEmail) {
      try {
        // Check if already read (need to cast or update interface)
        const memoRef = doc(db, `schools/${schoolID}/memos`, memo.id);
        await updateDoc(memoRef, {
          readBy: arrayUnion(userEmail)
        });
        // Optimistic update
        setMemos(prev => prev.map(m => m.id === memo.id ? { ...m, readBy: [...(m as any).readBy || [], userEmail] } : m));
      } catch (error) {
        console.error("Error marking memo as read:", error);
      }
    }
  };

  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;
    fetchUsers();
    const unsubscribeConv = fetchConversations();
    const unsubscribeMemos = fetchMemos();
    return () => {
      if (unsubscribeConv) unsubscribeConv();
      unsubscribeMemos();
    };
  }, [schoolID, isAuthLoading, hasValidClaims, fetchUsers, fetchConversations, fetchMemos]);

  const fetchConversationMessages = async (conversationId: string) => {
    try {
      setMessages([]);
      setIsLoadingMessages(true);
      const q = query(collection(db, `schools/${schoolID}/conversations/${conversationId}/messages`), orderBy("timestamp", "desc"));
      const snapshot = await getDocs(q);
      let messageList: Message[] = snapshot.docs.map(doc => ({
        id: doc.id,
        content: sanitizeHtml(doc.data().content || ''),
        senderEmail: sanitizeHtml(doc.data().senderEmail || ''),
        timestamp: doc.data().timestamp?.toDate()?.toLocaleString() || '',
        readBy: doc.data().readBy || [],
        priority: doc.data().priority || 'normal',
        deliveryMethod: doc.data().deliveryMethod || 'app',
      }));

      if (auth.currentUser?.email) {
        const userEmail = auth.currentUser.email;
        const messagesToUpdate = messageList.filter(msg => !msg.readBy.includes(userEmail));
        for (const msg of messagesToUpdate) {
          await updateDoc(doc(db, `schools/${schoolID}/conversations/${conversationId}/messages`, msg.id), {
            readBy: [...msg.readBy, auth.currentUser.email],
          });
        }
      }
      
      // Show all messages in conversation details view
      setMessages(messageList);
      
      // Mark unread messages as read in Firestore
      const currentUserEmail = auth.currentUser?.email;
      if (currentUserEmail) {
        const batch = writeBatch(db);
        let hasUpdates = false;
        
        for (const msg of messageList) {
          if (msg.senderEmail !== currentUserEmail && !msg.readBy.includes(currentUserEmail)) {
            const msgRef = doc(db, `schools/${schoolID}/conversations/${conversationId}/messages`, msg.id);
            batch.update(msgRef, {
              readBy: arrayUnion(currentUserEmail)
            });
            hasUpdates = true;
          }
        }
        
        if (hasUpdates) {
          await batch.commit();
        }
      }
      
      // Update conversation unread count to 0 and recalculate total
      setConversations(prev => {
        const updated = prev.map(conv => 
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        );
        const newTotal = updated.reduce((sum, conv) => sum + conv.unreadCount, 0);
        setUnreadCount(newTotal);
        return updated;
      });
    } catch (error) {
      console.error("Error fetching messages for conversation", conversationId, ":", error);
      toast({ title: "Error", description: "Failed to load messages.", variant: "destructive", duration: 5000 });
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleCategorySelect = (category: CommunicationCategory) => {
    if (category.disabled) {
      toast({ title: "Error", description: `No ${category.recipients.toLowerCase()}s available.`, variant: "destructive", duration: 5000 });
      return;
    }
    setSelectedCategory(category);
    setSelectedRecipient("");
    setRecipientSearch("");
    setRecipientType(category.id === "teachers" ? "teacher" : "guardian");
    setActiveView("compose");
  };

  const handleRecipientSelect = (recipient: User) => {
    setSelectedRecipient(recipient.email);
    setRecipientSearch("");
    setSelectedCategory({
      ...selectedCategory!,
      title: `Send to ${recipient.name}`,
      description: `Direct message to ${recipient.name} (${recipient.role === 'school_staff' ? `Teacher - ${recipient.subject}` : `Parent of ${recipient.studentName}`})`,
      recipientCount: 1,
      recipients: `${recipient.name} - ${recipient.role === 'school_staff' ? `Teacher` : `Parent of ${recipient.studentName}`}`,
    });
  };

  const handleSendMessage = async () => {
    if (!schoolID || !hasValidClaims || !selectedCategory || !auth.currentUser?.email || !selectedRecipient) {
      toast({ title: "Error", description: "Missing permissions or recipient.", variant: "destructive", duration: 5000 });
      return;
    }
    if (!subject || !message) {
      toast({ title: "Error", description: "Subject and message required.", variant: "destructive", duration: 5000 });
      return;
    }
    try {
      const recipient = recipientType === "teacher" ? teachers.find(t => t.email === selectedRecipient) : guardians.find(g => g.email === selectedRecipient);
      if (!recipient) throw new Error("Recipient not found.");
      const recipients = `${recipient.name} - ${recipient.role === 'school_staff' ? `Teacher` : `Parent of ${recipient.studentName}`}`;
      const sortedEmails = [auth.currentUser.email, selectedRecipient].sort();
      const convQuery = query(
        collection(db, `schools/${schoolID}/conversations`),
        where("type", "==", "1:1"),
        where("allowedEmails", "==", sortedEmails),
        where("schoolID", "==", schoolID)
      );
      const convSnapshot = await getDocs(convQuery);
      const conversationId = convSnapshot.empty
        ? (await addDoc(collection(db, `schools/${schoolID}/conversations`), {
            title: selectedCategory.title,
            allowedEmails: sortedEmails,
            participantRoles: ['student', recipientType === "teacher" ? 'school_staff' : 'guardian'],
            type: '1:1',
            lastMessage: null,
            lastUpdatedAt: serverTimestamp(),
            recipients,
            recipientCount: 1,
            schoolID,
          })).id
        : convSnapshot.docs[0].id;

      const newMessage = {
        content: sanitizeHtml(`${subject}\n\n${message}`),
        senderEmail: auth.currentUser.email,
        timestamp: serverTimestamp(),
        readBy: [auth.currentUser.email],
        priority,
        deliveryMethod,
      };
      await addDoc(collection(db, `schools/${schoolID}/conversations/${conversationId}/messages`), newMessage);
      await updateDoc(doc(db, `schools/${schoolID}/conversations`, conversationId), {
        lastMessage: { content: newMessage.content, senderEmail: auth.currentUser.email, timestamp: serverTimestamp(), priority },
        lastUpdatedAt: serverTimestamp(),
      });

      setMessage("");
      setSubject("");
      setPriority("normal");
      setDeliveryMethod("app");
      setSelectedRecipient("");
      setRecipientSearch("");
      setRecipientType(null);
      setActiveView("overview");
      setSelectedCategory(null);
      toast({ title: "Success", description: "Message sent successfully.", duration: 3000 });
    } catch (error: any) {
      toast({
        title: error.code === "permission-denied" ? "Permission Error" : "Error",
        description: error.message || "Failed to send message.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };



  const filteredRecipients = recipientType === "teacher"
    ? teachers.filter(t => t.name.toLowerCase().includes(recipientSearch.toLowerCase()) || t.email.toLowerCase().includes(recipientSearch.toLowerCase()))
    : guardians.filter(g => g.name.toLowerCase().includes(recipientSearch.toLowerCase()) || g.email.toLowerCase().includes(recipientSearch.toLowerCase()));

  const viewConversationDetails = (conv: Conversation, fromView: "received-messages" | "sent-messages") => {
    setSelectedConversation(conv);
    setReturnView(fromView);
    setMessages([]);
    setShowHistory(false);
    fetchConversationMessages(conv.id);
    setActiveView("conversation-details");
  };

  const getSenderName = (email: string) => {
    if (!email) return "Unknown Sender";
    if (email === auth.currentUser?.email) return "Me";
    return teachers.find(t => t.email === email)?.name || 
           guardians.find(g => g.email === email)?.name || 
           email;
  };

  const getConversationListTitle = (conv: Conversation) => {
    if (conv.type === '1:1') {
      const otherEmail = conv.allowedEmails.find(e => e !== auth.currentUser?.email);
      if (otherEmail) {
        return getSenderName(otherEmail);
      }
    }
    return conv.title;
  };

  const getDisplayTitle = () => {
    if (selectedConversation?.type === '1:1') {
      const otherEmail = selectedConversation.allowedEmails.find(e => e !== auth.currentUser?.email);
      if (otherEmail) {
        const otherName = getSenderName(otherEmail);
        return returnView === "received-messages" ? `From ${otherName}` : `To ${otherName}`;
      }
    }
    return selectedConversation?.title || 'Conversation';
  };

  const renderView = () => {
    if (isAuthLoading || !schoolID || !hasValidClaims) {
      return (
        <div className="flex-1 p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-2" />
            <BlueLoader />
          </div>
        </div>
      );
    }

    if (activeView === "conversation-details" && selectedConversation) {
      return (
        <div className="max-w-6xl mx-auto">
          <div className="mb-6 flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => setActiveView(returnView)} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to {returnView === "received-messages" ? "Inbox" : "Sent"}
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-6">
              {/* Email Subject Header */}
              <div className="bg-white dark:bg-[#020817] p-1">
                  <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">{getDisplayTitle()}</h1>
                  <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                      <Badge variant="secondary" className="rounded-md font-normal">{selectedConversation.type === '1:1' ? 'Direct Message' : 'Group Announcement'}</Badge>
                      <span>&bull;</span>
                      <span>{selectedConversation.recipients}</span>
                  </div>
              </div>

              {/* Message Thread */}
              <div className="space-y-4">
                  {isLoadingMessages ? (
                    <div className="flex justify-center py-12">
                      <BlueLoader />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-12 bg-white dark:bg-[#020817] rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
                      <p className="text-gray-500">No messages in this conversation.</p>
                    </div>
                  ) : (
                    <>
                      {(() => {
                        const unreadMessages = messages.filter(m => !m.readBy.includes(auth.currentUser?.email || ''));
                        let displayedMessages = messages;
                        
                        if (!showHistory) {
                          if (unreadMessages.length > 0) {
                            displayedMessages = unreadMessages;
                          } else {
                            displayedMessages = [messages[0]];
                          }
                        }

                        return (
                          <>
                            {displayedMessages.map((msg) => {
                              const senderName = getSenderName(msg.senderEmail);
                              const isMe = msg.senderEmail === auth.currentUser?.email;
                              
                              let displaySubject = "No Subject";
                              let displayBody = msg.content;
                              
                              const parts = msg.content.split('\n\n');
                              if (parts.length >= 2) {
                                displaySubject = parts[0];
                                displayBody = parts.slice(1).join('\n\n');
                              } else {
                                  const partsSingle = msg.content.split('\n');
                                  if (partsSingle.length >= 2) {
                                      displaySubject = partsSingle[0];
                                      displayBody = partsSingle.slice(1).join('\n');
                                  }
                              }

                              return (
                                <Card key={msg.id} className="border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden bg-white dark:bg-[#020817] mb-6">
                                  <div className="p-0">
                                      <div className="bg-gray-50/80 dark:bg-gray-900/50 p-6 border-b border-gray-100 dark:border-gray-800 space-y-3">
                                          <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">To</span>
                                              <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{isMe ? selectedConversation.recipients : 'Me'}</span>
                                          </div>
                                          <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">From</span>
                                              <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{senderName} &lt;{msg.senderEmail}&gt;</span>
                                          </div>
                                          <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Date</span>
                                              <span className="text-sm text-gray-600 dark:text-gray-400">{msg.timestamp}</span>
                                          </div>
                                          <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Subject</span>
                                              <span className="text-sm font-semibold text-gray-900 dark:text-white">{displaySubject}</span>
                                          </div>
                                      </div>
                                      
                                      <div className="p-6 sm:p-8">
                                          <div className="text-base leading-7 text-gray-700 dark:text-gray-300 font-normal whitespace-pre-wrap break-words font-sans">
                                              {displayBody}
                                          </div>
                                      </div>
                                      
                                      {isMe && (
                                          <div className="px-6 py-3 bg-gray-50/50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-800 flex justify-end">
                                              <div className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
                                                  {msg.readBy.length > 1 ? (
                                                      <>
                                                          <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                                                          <span className="text-green-700 dark:text-green-500">Read by {msg.readBy.length - 1} recipient(s)</span>
                                                      </>
                                                  ) : (
                                                      <span>Sent</span>
                                                  )}
                                              </div>
                                          </div>
                                      )}
                                  </div>
                                </Card>
                              );
                            })}
                            
                            {!showHistory && messages.length > displayedMessages.length && (
                              <div className="flex justify-center pt-4">
                                <Button 
                                  variant="outline" 
                                  onClick={() => setShowHistory(true)}
                                  className="flex items-center gap-2 text-gray-500"
                                >
                                  <History className="h-4 w-4" />
                                  Show Previous Messages ({messages.length - displayedMessages.length})
                                </Button>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
              </div>
            </div>

            {/* Sidebar Context */}
            <div className="space-y-6 lg:sticky lg:top-6 lg:self-start">
              <Card className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#020817] shadow-sm">
                <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
                  <CardTitle className="text-base font-medium">About this conversation</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Participants</div>
                      <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 dark:text-gray-300">Total Recipients</span>
                          <Badge variant="secondary" className="font-normal">{selectedConversation.recipientCount}</Badge>
                      </div>
                  </div>
                  
                  <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Roles Involved</div>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedConversation.participantRoles.map((role, index) => (
                          <Badge key={index} variant="outline" className="text-xs font-normal capitalize bg-gray-50 dark:bg-gray-900">{role.replace('_', ' ')}</Badge>
                        ))}
                      </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      );
    }

    if (activeView === "received-messages") {
      const receivedConvs = conversations.filter(conv => {
        // Show conversation if it has any messages from others OR if student is just a recipient
        return conv.hasReceivedMessages || (conv.allowedEmails.includes(auth.currentUser?.email || '') && conv.participantRoles.includes('admin'));
      });
      return (
        <div className="max-w-6xl mx-auto">
          <div className="mb-6 flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Received Messages</h1>
              <p className="text-gray-600 dark:text-gray-400">Messages sent to you</p>
            </div>
          </div>
          <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Received Messages</CardTitle>
              <CardDescription>Messages addressed to you</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {receivedConvs.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-4">No messages received yet.</p>
              ) : (
                <div className="space-y-4">
                  {receivedConvs.map((conv) => {
                    const senderEmail = conv.lastMessage?.senderEmail;
                    const senderName = senderEmail ? senderEmail.split('@')[0] : 'Unknown';
                    const fromLabel = conv.type === '1:1' ? `From: ${senderName}` : `From Group: ${conv.title}`;
                    return (
                      <div
                        key={conv.id}
                        className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B] cursor-pointer transition-colors"
                        onClick={() => viewConversationDetails(conv, "received-messages")}
                      >
                        <div className="flex items-center justify-between p-4">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {conv.recipients.includes("All students, staff, and parents") && <Users className="h-4 w-4 text-blue-500" />}
                              {conv.recipients.includes("Teachers, administrators, and support staff") && <GraduationCap className="h-4 w-4 text-green-500" />}
                              {conv.recipients.includes("Parents and legal guardians") && <UserCheck className="h-4 w-4 text-purple-500" />}
                              {conv.recipients.includes("All enrolled students") && <Users className="h-4 w-4 text-orange-500" />}
                              {conv.type === "1:1" && <UserCheck className="h-4 w-4 text-blue-600" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium truncate">{getConversationListTitle(conv)}</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{fromLabel} • {conv.recipientCount} recipients • {conv.lastUpdatedAt}</p>
                              {conv.lastMessage && (
                                <p className="text-xs text-gray-500 truncate">Last: {truncateMessage(conv.lastMessage.content, 50)}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {conv.unreadCount > 0 && (
                              <Badge variant="destructive" className="text-xs">{conv.unreadCount} New</Badge>
                            )}
                            <Badge variant="outline" className="text-xs hidden sm:inline-flex border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-2 py-0.5">{conv.type}</Badge>
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); viewConversationDetails(conv, "received-messages"); }} className="flex items-center gap-1">
                              <Eye className="h-3 w-3" /> <span className="hidden sm:inline">View</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    if (activeView === "sent-messages") {
      const sentConvs = conversations.filter(conv => conv.lastMessage && conv.lastMessage.senderEmail === auth.currentUser?.email);
      return (
        <div className="max-w-6xl mx-auto">
          <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveView("overview")}
              className="flex items-center gap-2 w-fit"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Communication</span>
              <span className="sm:hidden">Back</span>
            </Button>
            <div className="flex-1">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">Sent Messages</h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Messages you have sent</p>
            </div>
          </div>
          <Card className="m-2 dark:bg-gray-800 dark:border-gray-700">
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg"><Send className="h-4 w-4 sm:h-5 sm:w-5" />Sent Messages</CardTitle>
              <CardDescription className="text-xs sm:text-sm"></CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 space-y-3 sm:space-y-4">
              {sentConvs.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-4">No messages sent.</p>
              ) : (
                sentConvs.map((conv) => (
                  <div
                    key={conv.id}
                    className="m-2 flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <div className="flex items-start gap-3 flex-1 w-full min-w-0">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {conv.recipients.includes("Teacher") ? <GraduationCap className="h-4 w-4 text-green-500" /> : <Users className="h-4 w-4 text-blue-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm sm:text-base truncate mb-1">{conv.title}</h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">
                          To: {truncateRecipients(conv.recipients)} • {conv.lastUpdatedAt.split(', ')[0]}
                        </p>
                        {conv.lastMessage && <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">{truncateMessage(conv.lastMessage.content)}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 sm:mt-0 flex-wrap sm:flex-nowrap self-start sm:self-center flex-shrink-0">
                      <Badge variant="outline" className="text-xs hidden sm:inline-flex border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-2 py-0.5">1:1</Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); viewConversationDetails(conv, "sent-messages"); }}
                        className="flex items-center gap-1 text-xs sm:text-sm"
                      >
                        <Eye className="h-3 w-3" />
                        <span className="hidden sm:inline">View</span>
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    if (activeView === "compose" && selectedCategory) {
      const Icon = selectedCategory.icon;

      return (
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveView("overview")}
              className="flex items-center gap-2 w-fit"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
              <span className="sm:hidden">Back</span>
            </Button>
            <div className="flex-1">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{selectedCategory.title}</h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{truncateRecipients(selectedCategory.recipients)}</p>
            </div>
          </div>
          {/* Adjusted grid layout for desktop: increased compose message width, reduced recipient/delivery preview width */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3"> {/* Changed sm:grid-cols-2 to sm:grid-cols-3 for finer control */}
            {/* Increased width of compose message box on desktop */}
            <div className="sm:col-span-2 "> {/* Change sm:col-span-2 to sm:col-span-1 or adjust to sm:col-span-3 for different widths */}
              <Card className="m-2 border -mt-5 border-[#E2E8F0] dark:border-gray-800 bg-[#FFFFFF] dark:bg-[#020817]">
                <CardHeader className="p-3 sm:p-6">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">{React.createElement(Icon, { className: "h-4 w-4 sm:h-5 sm:w-5" })}Compose Message</CardTitle>
                </CardHeader>
                <CardContent className="space-y-0.5 p-3 sm:p-6"> {/* Reduced spacing from space-y-4 to space-y-2 */}
                  <div>
                    <label className="block text-sm font-medium mb-2 -mt-7">Recipient</label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full justify-between text-sm sm:text-base"><span>{selectedRecipient || `Select a ${recipientType}`}</span><ChevronDown className="h-4 w-4 opacity-50" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-full min-w-[300px] sm:min-w-[400px] bg-white dark:bg-gray-800">
                        <div className="p-2">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                            <Input placeholder={`Search ${recipientType}s...`} value={recipientSearch} onChange={(e) => setRecipientSearch(sanitizeHtml(e.target.value))} className="pl-7 sm:pl-8 text-xs sm:text-sm h-8 sm:h-10" />
                          </div>
                        </div>
                        <DropdownMenuSeparator />
                        <div className="max-h-40 overflow-y-auto">
                          {filteredRecipients.length > 0 ? filteredRecipients.map(recipient => (
                            <DropdownMenuItem key={recipient.email} onClick={() => handleRecipientSelect(recipient)} className="flex flex-col items-start p-2 sm:p-3">
                              <div className="font-medium text-xs sm:text-sm">{recipient.name}</div>
                              <div className="text-xs text-gray-500">{recipientType === "teacher" ? recipient.subject : `Parent of ${recipient.studentName}`}</div>
                            </DropdownMenuItem>
                          )) : (
                            <DropdownMenuItem disabled className="text-center py-3 sm:py-4 text-gray-500 text-xs sm:text-sm">No {recipientType}s found</DropdownMenuItem>
                          )}
                        </div>
                        {selectedRecipient && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => { setSelectedRecipient(""); setRecipientSearch(""); setSelectedCategory(communicationCategories.find(cat => cat.id === selectedCategory.id)!); }} className="text-red-600 text-xs sm:text-sm">Clear</DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {selectedRecipient && (
                      <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center gap-2">
                          {recipientType === "teacher" ? <GraduationCap className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" /> : <Users className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />}
                          <span className="text-xs sm:text-sm font-medium text-blue-800 dark:text-blue-200">Selected: {selectedRecipient}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Subject</label>
                    <Input value={subject} onChange={(e) => setSubject(sanitizeHtml(e.target.value))} placeholder="Enter subject..." className="w-full text-sm sm:text-base" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Priority</label>
                      <Select value={priority} onValueChange={setPriority}>
                        <SelectTrigger className="text-sm sm:text-base"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Delivery</label>
                      <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
                        <SelectTrigger className="text-sm sm:text-base"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="app">App</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="sms">SMS</SelectItem>
                          <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Message</label>
                    <Textarea value={message} onChange={(e) => setMessage(sanitizeHtml(e.target.value))} placeholder="Type your message..." className="min-h-[120px] border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-sm sm:text-base" />
                  </div>
                  <div className="flex flex-col sm:flex-row justify-end gap-3">
                    <Button variant="outline" className="w-full mt-1 sm:w-auto" onClick={() => setActiveView("overview")}>Cancel</Button>
                    <Button onClick={handleSendMessage} className="dark:bg-[#3576DF] mt-1 w-full sm:w-auto" disabled={!message || !subject || !selectedRecipient}><Send className="h-4 w-4 mr-2 " />Send</Button>
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* Reduced width of recipient and delivery preview on desktop, reduced spacing between recipient and delivery preview cards from space-y-4 sm:space-y-6 to space-y-2 sm:space-y-3 */}
            <div className="space-y-2 sm:space-y-3 sm:col-span-1">
              <Card className="m-2 dark:bg-gray-800 dark:border-gray-700">
                <CardHeader className="p-3 sm:p-6"><CardTitle className="text-base sm:text-lg">Recipient</CardTitle></CardHeader>
                <CardContent className="p-3 sm:p-6"><div className="flex items-center gap-2"><div className={`w-3 h-3 rounded-full ${selectedCategory.color}`} /><span className="text-xs sm:text-sm">{truncateRecipients(selectedCategory.recipients)}</span></div></CardContent>
              </Card>
              <Card className="m-2 dark:bg-gray-800 dark:border-gray-700">
                <CardHeader className="p-3 sm:p-6"><CardTitle className="text-base sm:text-lg">Delivery Preview</CardTitle></CardHeader>
                <CardContent className="space-y-3 p-3 sm:p-6">
                  <div className="flex items-center gap-2">
                    {deliveryMethod === "app" && <Bell className="h-3 w-3 sm:h-4 sm:w-4" />}
                    {deliveryMethod === "email" && <Mail className="h-3 w-3 sm:h-4 sm:w-4" />}
                    {deliveryMethod === "sms" && <Smartphone className="h-3 w-3 sm:h-4 sm:w-4" />}
                    {deliveryMethod === "all" && <Users className="h-3 w-3 sm:h-4 sm:w-4" />}
                    <span className="text-xs sm:text-sm capitalize">{deliveryMethod} delivery</span>
                  </div>
                  <div className="flex items-center gap-2"><Clock className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" /><span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Estimated delivery: 2-5 min</span></div>
                  {priority === "urgent" && <Badge variant="destructive" className="text-xs">Urgent</Badge>}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-full sm:max-w-6xl mx-auto overflow-x-hidden">
        <div className="mb-6 flex justify-center">
          <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-lg inline-flex">
            <button
              onClick={() => { setActiveTab("messages"); setActiveView("overview"); setSelectedCategory(null); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "messages" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"}`}
            >
              Messages
            </button>
            <button
              onClick={() => { setActiveTab("memos"); setActiveView("overview"); setSelectedCategory(null); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "memos" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"} flex items-center gap-2`}
            >
              Memos
              {memos.filter(m => !m.readBy?.includes(auth.currentUser?.email || '')).length > 0 && (
                <Badge className="h-5 w-5 p-0 flex items-center justify-center rounded-full text-[10px] bg-red-500 hover:bg-red-600 text-white">
                  {memos.filter(m => !m.readBy?.includes(auth.currentUser?.email || '')).length}
                </Badge>
              )}
            </button>
          </div>
        </div>

        {activeTab === "messages" ? (
          <>
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
          <div className="w-full">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 truncate">Student Communication Center</h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Send messages to teachers or guardians</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button onClick={() => setActiveView("received-messages")} variant="outline" className="flex items-center gap-2 w-full sm:w-auto">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Received</span>
              <span className="sm:hidden">Received</span>
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {unreadCount}
                </Badge>
              )}
            </Button>
            <Button onClick={() => setActiveView("sent-messages")} variant="outline" className="flex items-center gap-2 w-full sm:w-auto">
              <Send className="h-4 w-4" />
              <span className="hidden sm:inline">Sent</span>
              <span className="sm:hidden">Sent</span>
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8">
          {communicationCategories.map(category => (
            <Card
              key={category.id}
              className={`hover:shadow-lg transition-all cursor-pointer border-l-4 dark:bg-[#020817] border-[#E2E8F0] dark:border-gray-700 ${category.disabled ? 'opacity-50 cursor-not-allowed' : ''} w-full max-w-full overflow-hidden`}
              style={{ borderLeftColor: category.color === 'bg-green-500' ? '#10b981' : '#3b82f6' }}
              onClick={() => handleCategorySelect(category)}
            >
              <CardHeader className="pb-3 p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 w-full">
                    <div className={`p-2 ${category.color} rounded-lg flex-shrink-0`}>{React.createElement(category.icon, { className: "h-5 w-5 text-white" })}</div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base sm:text-lg truncate">{category.title}</CardTitle>
                      <CardDescription className="text-xs sm:text-sm truncate">{category.description}</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{truncateRecipients(category.recipients)}</span>
                  <Badge className="dark:border-[#1E293B] border-[#E2E8F0] bg-[#FFFFFF] dark:bg-[#020817] dark:hover:bg-[#101828] text-[#fffff] text-xs">{category.recipientCount} recipients</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="m-2 dark:bg-[#020817] border border-[#E2E8F0] dark:border-gray-700 w-full max-w-full overflow-hidden">
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg"><MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />Recent Messages</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Your latest communication activity</CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 space-y-3 sm:space-y-4">
            {conversations.filter(conv => conv.hasReceivedMessages).length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">No recent messages.</p>
            ) : (
              conversations.filter(conv => conv.hasReceivedMessages).slice(0, 5).map(conv => (
                <div
                  key={conv.id}
                  className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] dark:bg-[#020817] flex flex-col items-start p-3 sm:p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-[#101929] w-full max-w-full overflow-hidden"
                  onClick={() => viewConversationDetails(conv, "received-messages")}
                >
                  <div className="flex items-start gap-3 flex-1 w-full">
                    <div className="flex items-center gap-2 flex-shrink-0">{conv.recipients.includes("Teacher") ? <GraduationCap className="h-4 w-4 text-green-500" /> : <Users className="h-4 w-4 text-blue-500" />}</div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm sm:text-base truncate">{conv.title}</h4>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">From: {truncateRecipients(conv.recipients)} • {conv.lastUpdatedAt.split(', ')[0]}</p>
                      {conv.lastMessage && <p className="text-xs sm:text-sm text-gray-500 line-clamp-1">{truncateMessage(conv.lastMessage.content)}</p>}
                    </div>
                  </div>
                  {conv.lastMessage?.priority === "urgent" && <Badge variant="destructive" className="text-xs mt-2 self-start">Urgent</Badge>}
                </div>
              ))
            )}
          </CardContent>
        </Card>
        </>
        ) : (
          <>
            {selectedMemo ? (
              <div className="flex flex-col h-full max-w-6xl mx-auto">
                <div className="mb-4 flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setSelectedMemo(null)} className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Button>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">{selectedMemo.title}</h1>
                </div>
                
                <div className="mb-6 bg-white dark:bg-[#020817] border border-gray-200 dark:border-gray-800 rounded-lg p-6 shadow-sm">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-full">
                        <GraduationCap className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">School Administration</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Official Memo</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs font-normal">
                            {selectedMemo.targetAudience === 'everyone' ? 'Public' : 'Internal'}
                          </Badge>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">{selectedMemo.createdAt}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 md:max-w-md">
                       <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Subject: {selectedMemo.title}</h4>
                       <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                         {selectedMemo.description || "No additional description provided."}
                       </p>
                    </div>
                  </div>
                </div>

                <div className="flex-1 bg-gray-100 dark:bg-gray-900 rounded-md overflow-hidden border border-gray-200 dark:border-gray-800 min-h-[600px]">
                  {selectedMemo.fileUrl && (
                    <iframe src={selectedMemo.fileUrl} className="w-full h-full" title={selectedMemo.title} />
                  )}
                </div>
              </div>
            ) : activeMemoCategory ? (
              <div className="max-w-6xl mx-auto">
                <div className="mb-4 flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveMemoCategory(null)} className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back to Memos
                  </Button>
                  <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                      {memoCategories.find(c => c.id === activeMemoCategory)?.title}
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {memoCategories.find(c => c.id === activeMemoCategory)?.description}
                    </p>
                  </div>
                </div>
                <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardContent className="space-y-4 p-4">
                    {memos.filter(m => m.targetAudience === activeMemoCategory).length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No memos in this category.</p>
                    ) : (
                      memos.filter(m => m.targetAudience === activeMemoCategory).map((memo) => (
                        <div key={memo.id} className="border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B] transition-colors">
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <FileText className="h-8 w-8 text-blue-500 flex-shrink-0 p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg" />
                              <div className="min-w-0 flex-1">
                                <h4 className="font-medium truncate text-base">{memo.title}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                  {memo.description || "No description"} • {memo.createdAt}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Button variant="outline" size="sm" className="flex items-center gap-1" onClick={() => handleViewMemo(memo)}>
                                <Eye className="h-3 w-3" /> <span className="hidden sm:inline">View</span>
                              </Button>
                              <a href={memo.fileUrl} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="flex items-center gap-1">
                                  <Download className="h-3 w-3" /> <span className="hidden sm:inline">Download</span>
                                </Button>
                              </a>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="max-w-6xl mx-auto">
                <div className="mb-4 sm:mb-6 space-y-4">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">Memo Management</h1>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">View official memos</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 mb-4 sm:mb-8">
                  {memoCategories.map((category) => {
                    const count = memos.filter(m => m.targetAudience === category.id && !m.readBy?.includes(auth.currentUser?.email || '')).length;
                    return (
                      <Card
                        key={category.id}
                        className={`border border-[#E2E8F0] dark:border-[#1E293B] hover:shadow-lg transition-all cursor-pointer border-l-4 bg-[#FFFFFF] dark:bg-[#020817] ${category.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        style={{ borderLeftColor: category.color.replace('bg-', '') === 'blue-500' ? '#3b82f6' : category.color.replace('bg-', '') === 'green-500' ? '#10b981' : category.color.replace('bg-', '') === 'purple-500' ? '#8b5cf6' : '#f97316' }}
                        onClick={() => setActiveMemoCategory(category.id)}
                      >
                        <CardHeader className="p-3 sm:pb-3">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className={`p-1.5 sm:p-2 ${category.color} rounded-lg`}>
                              {React.createElement(category.icon, { className: "h-4 w-4 sm:h-5 sm:w-5 text-white" })}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-sm sm:text-lg line-clamp-1">{category.title}</CardTitle>
                                {count > 0 && (
                                  <Badge className="bg-red-500 hover:bg-red-600 text-white border-none">
                                    {count}
                                  </Badge>
                                )}
                              </div>
                              <CardDescription className="text-xs sm:text-sm line-clamp-2">{category.description}</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-1">{category.recipients}</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Recent Memos</CardTitle>
                    <CardDescription>Recently uploaded memos</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {memos.length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No memos uploaded yet.</p>
                    ) : (
                      memos.slice(0, 5).map((memo) => (
                        <div key={memo.id} className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B]">
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <h4 className="font-medium truncate">{memo.title}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">Target: {memo.targetAudience} • {memo.createdAt}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Button variant="outline" size="sm" className="flex items-center gap-1" onClick={() => handleViewMemo(memo)}>
                                <Eye className="h-3 w-3" /> <span className="hidden sm:inline">View</span>
                              </Button>
                              <a href={memo.fileUrl} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="flex items-center gap-1">
                                  <Download className="h-3 w-3" /> <span className="hidden sm:inline">Download</span>
                                </Button>
                              </a>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <StudentSidebar />
        <div className="flex flex-col flex-1 h-full">
          <StudentHeader />
          <main className="flex-1 p-3 sm:p-6 overflow-auto bg-gray-50 dark:bg-gray-900 overflow-x-hidden">{renderView()}</main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default StudentCommunication;